import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { WorkbenchEmpty, WorkbenchNotice, WorkbenchStat } from '@ndrc/ui';
import { useDesk } from '../desk/context';
import { callCore } from '../lib/rpc';
import { t } from '../messages';
import { PracticeEditor } from './PracticeEditor';
import { PracticeReview } from './PracticeReview';
import { PracticeFeedback } from './PracticeFeedback';
import { PracticeFileLinks } from './PracticeAnnexFiles';
import { usePracticeRequest } from './usePracticeRequest';
import { dateLabel, type Assignment, type PracticeRead, type SavedDraft } from './model';
export function PracticeBoard({ classId, teacher }: { classId: string; teacher: boolean }) {
  const [selected, select] = useState(''),
    [offset, setOffset] = useState(0);
  const { observing } = useDesk();
  const cache = useQueryClient();
  const query = useQuery({
    queryKey: ['core', 'practice.list', classId, offset],
    queryFn: () =>
      callCore<{ total: number; items: Assignment[] }>('practice.list', { classId, offset }),
    refetchInterval: 10000,
  });
  const items = (query.data?.items ?? []).filter(
    (item) => !observing || item.userId === observing.userId,
  );
  const id = selected || items[0]?.id;
  return (
    <div className="practice-split">
      <aside className="practice-rail" aria-label={t('practice.assignments')}>
        <div className="lab-toolbar">
          <strong>{t('practice.assignments')}</strong>
          <button
            type="button"
            disabled={query.isFetching}
            onClick={() => {
              void query.refetch();
              void cache.invalidateQueries({ queryKey: ['core', 'practice.read', classId] });
              void cache.invalidateQueries({ queryKey: ['core', 'practice.reviews', classId] });
            }}
          >
            {t('practice.refresh')}
          </button>
        </div>
        {query.isPending && <p role="status">{t('practice.loading')}</p>}
        {query.isError && <WorkbenchNotice error>{query.error.message}</WorkbenchNotice>}
        {items.map((item) => (
          <button
            type="button"
            className="practice-assignment"
            key={item.id}
            aria-pressed={item.id === id}
            onClick={() => select(item.id)}
          >
            <strong>{item.title}</strong>
            {teacher && <span>{item.userName}</span>}
            <span>
              {t(item.format === 'cms' ? 'practice.cms' : 'practice.written')}
              <span aria-hidden="true"> · </span>
              {t('practice.minutes', { count: item.durationSeconds / 60 })}
            </span>
            <span className="practice-badge">
              {t(
                item.submittedAt
                  ? 'practice.submitted'
                  : item.expired
                    ? 'practice.expired'
                    : item.startedAt
                      ? 'practice.running'
                      : 'practice.ready',
              )}
            </span>
          </button>
        ))}
        {!query.isPending && !query.isError && !items.length && (
          <WorkbenchEmpty title={t('practice.empty')} />
        )}
        <div className="lab-toolbar">
          <button
            type="button"
            disabled={offset === 0 || query.isFetching}
            onClick={() => {
              setOffset(Math.max(0, offset - 30));
              select('');
            }}
          >
            {t('practice.previous')}
          </button>
          <button
            type="button"
            disabled={offset + 30 >= (query.data?.total ?? 0) || query.isFetching}
            onClick={() => {
              setOffset(offset + 30);
              select('');
            }}
          >
            {t('practice.next')}
          </button>
        </div>
      </aside>
      <section className="practice-detail">
        {id ? (
          <PracticeDetail key={id} classId={classId} assignmentId={id} teacher={teacher} />
        ) : (
          <WorkbenchEmpty title={t('practice.choose')}>
            <span>{t('practice.emptyHint')}</span>
          </WorkbenchEmpty>
        )}
      </section>
    </div>
  );
}
function PracticeDetail({
  classId,
  assignmentId,
  teacher,
}: {
  classId: string;
  assignmentId: string;
  teacher: boolean;
}) {
  const { me, observing, workspace } = useDesk();
  const readOnly = Boolean(observing) || workspace?.readOnly === true;
  const [submissionId, selectSubmission] = useState<string>(),
    [offset, setOffset] = useState(0);
  const request = usePracticeRequest(`ndrc-practice:${me.id}:${classId}:${assignmentId}:command`);
  const query = useQuery({
    queryKey: ['core', 'practice.read', classId, assignmentId, submissionId, offset],
    queryFn: () =>
      callCore<PracticeRead>('practice.read', { classId, assignmentId, submissionId, offset }),
    refetchInterval: 10000,
  });
  async function command(op: string, body: Record<string, unknown> = {}) {
    if (readOnly) return;
    const result = await request.execute(op, {
      classId,
      ...(op === 'practice.release' ? {} : { assignmentId }),
      ...body,
    });
    if (result !== undefined) await query.refetch();
  }
  const data = query.data;
  if (!data)
    return query.isError ? (
      <WorkbenchNotice error>{query.error.message}</WorkbenchNotice>
    ) : (
      <p role="status">{t('practice.loading')}</p>
    );
  const copy = data.attempt;
  return (
    <>
      <header className="practice-heading">
        <span className="practice-kicker">
          {t(data.assignment.format === 'cms' ? 'practice.cms' : 'practice.written')}
        </span>
        <h2>{data.assignment.title}</h2>
        <p>{t('practice.durationHint', { count: data.assignment.durationSeconds / 60 })}</p>
      </header>
      {readOnly && <WorkbenchNotice>{t('practice.readOnly')}</WorkbenchNotice>}
      {(query.isError || request.error) && (
        <WorkbenchNotice error>{request.error || query.error?.message}</WorkbenchNotice>
      )}
      {!request.stored && <WorkbenchNotice error>{t('practice.storageError')}</WorkbenchNotice>}
      {request.pending && !request.busy && (
        <WorkbenchNotice>
          <p>{t('practice.pending')}</p>
          <button
            type="button"
            disabled={readOnly}
            onClick={() => {
              void command(request.pending!.op, request.pending!.body);
            }}
          >
            {t('practice.retry')}
          </button>
        </WorkbenchNotice>
      )}
      {!copy && !teacher && (
        <div className="practice-card">
          <h3>{t('practice.beforeStart')}</h3>
          <p>{t('practice.startHint')}</p>
          <button
            type="button"
            className="lab-primary"
            disabled={readOnly || request.busy || Boolean(request.pending)}
            onClick={() => {
              void command('practice.start');
            }}
          >
            {t('practice.start')}
          </button>
        </div>
      )}
      {data.definition && (
        <>
          <div className="practice-card">
            <h3>{t('practice.situation')}</h3>
            <p className="practice-prose">{data.definition.situation}</p>
            <h3>{t('practice.instructions')}</h3>
            <p className="practice-prose">{data.definition.instructions}</p>
            {data.definition.annexes.map((annex, index) => (
              <details key={index}>
                <summary>{annex.title}</summary>
                <p className="practice-prose">{annex.text}</p>
              </details>
            ))}
            <PracticeFileLinks files={data.files ?? []} />
            <details>
              <summary>{t('practice.rubric')}</summary>
              <ul>
                {data.definition.rubric.map((criterion) => (
                  <li key={criterion.id}>
                    {criterion.label}
                    {criterion.maxPoints !== null && (
                      <span>{t('practice.maxPoints', { count: criterion.maxPoints })}</span>
                    )}
                  </li>
                ))}
              </ul>
            </details>
            {data.hints.length > 0 && (
              <details>
                <summary>{t('practice.hints')}</summary>
                {data.hints.map((hint, i) => (
                  <p className="practice-prose" key={i}>
                    {hint}
                  </p>
                ))}
              </details>
            )}
          </div>
        </>
      )}
      {copy && !teacher && (
        <PracticeEditor
          key={assignmentId}
          classId={classId}
          attempt={copy}
          cms={data.assignment.format === 'cms'}
          readOnly={readOnly}
          refresh={async () => {
            await query.refetch();
          }}
        />
      )}
      {copy && teacher && (
        <div className="practice-card">
          <div className="lab-stats">
            <WorkbenchStat
              label={t('practice.lastSave')}
              value={copy.savedAt ? dateLabel(copy.savedAt) : t('practice.notSaved')}
            />
            <WorkbenchStat label={t('practice.deadline')} value={dateLabel(copy.deadlineAt)} />
          </div>
          <h3>{t('practice.savedDraft')}</h3>
          <SavedProduction body={copy.draft} />
          {!copy.submittedAt && !readOnly && (
            <details>
              <summary>{t('practice.close')}</summary>
              <p>{t('practice.closeHint')}</p>
              <button
                type="button"
                disabled={request.busy || Boolean(request.pending)}
                onClick={() => {
                  void command('practice.capture', { revision: copy.revision, final: true });
                }}
              >
                {t('practice.confirmClose')}
              </button>
            </details>
          )}
        </div>
      )}
      {copy && (
        <div className="practice-card">
          <h3>{t('practice.versions')}</h3>
          <p>{t('practice.versionHint')}</p>
          <div className="lab-actions">
            {data.submissions.map((submission) => (
              <button
                type="button"
                key={submission.id}
                aria-pressed={data.selectedSubmission?.id === submission.id}
                onClick={() => selectSubmission(submission.id)}
              >
                {t(submission.final ? 'practice.finalVersion' : 'practice.version', {
                  revision: submission.revision,
                })}
              </button>
            ))}
          </div>
          {data.totalSubmissions > 20 && (
            <div className="lab-actions">
              <button
                type="button"
                disabled={offset === 0}
                onClick={() => {
                  setOffset(Math.max(0, offset - 20));
                  selectSubmission(undefined);
                }}
              >
                {t('practice.previous')}
              </button>
              <button
                type="button"
                disabled={offset + 20 >= data.totalSubmissions}
                onClick={() => {
                  setOffset(offset + 20);
                  selectSubmission(undefined);
                }}
              >
                {t('practice.next')}
              </button>
            </div>
          )}
          {data.selectedSubmission ? (
            <>
              <p>{dateLabel(data.selectedSubmission.createdAt)}</p>
              <SavedProduction body={data.selectedSubmission.body} />
              <PracticeFeedback
                key={data.selectedSubmission.id}
                classId={classId}
                assignmentId={assignmentId}
                submissionId={data.selectedSubmission.id}
                rubric={data.definition?.rubric ?? []}
              />
              {teacher && data.definition && (copy.submittedAt || copy.expired) && !readOnly && (
                <PracticeReview
                  key={data.selectedSubmission.id}
                  classId={classId}
                  submissionId={data.selectedSubmission.id}
                  rubric={data.definition.rubric}
                  refresh={async () => {
                    await query.refetch();
                  }}
                />
              )}
            </>
          ) : (
            <p>{t('practice.noVersions')}</p>
          )}
        </div>
      )}
      {data.solution && (
        <div className="practice-card">
          <h3>{t('practice.solution')}</h3>
          <p className="practice-prose">{data.solution}</p>
        </div>
      )}
      {teacher && data.privateSolution && (
        <details className="practice-card">
          <summary>{t('practice.privateSolution')}</summary>
          <p className="practice-prose">{data.privateSolution}</p>
          {!data.solution && !readOnly && (
            <>
              <p>{t('practice.releaseHint')}</p>
              <button
                type="button"
                disabled={request.busy || Boolean(request.pending)}
                onClick={() => {
                  void command('practice.release', { subjectId: data.assignment.subjectId });
                }}
              >
                {t('practice.release')}
              </button>
            </>
          )}
        </details>
      )}
    </>
  );
}
export function SavedProduction({ body }: { body: SavedDraft }) {
  return (
    <>
      <p className="practice-prose">{body.response || t('practice.noResponse')}</p>
      {body.sources.map((source) => (
        <details key={`${source.environmentId}:${source.kind}:${source.externalId}`}>
          <summary>
            {source.environmentLabel}
            <span aria-hidden="true"> · </span>
            {source.record.title}
          </summary>
          <p>{t('practice.observed', { date: dateLabel(source.observedAt) })}</p>
          <dl className="practice-record">
            {source.record.fields.map((field, i) => (
              <div key={i}>
                <dt>{field.label}</dt>
                <dd>{field.value}</dd>
              </div>
            ))}
          </dl>
        </details>
      ))}
    </>
  );
}
